import React, { useState, useEffect } from 'react';
import { Box, useTheme } from '@mui/material';
import ProgressionTab from '../../../../../theme/ProgressionBuilder/ProgressionTab';
import Text from '../../../../../theme/text/Text';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { tokens } from '../../../../../../theme';
import { setTabErrors } from '../../../../../../newapi/global/progressionTabSlice';
import ShValidatedInput from '../../../../../theme/inputs/ShValidatedInput';
import ShValidatedInputDropdown from '../../../../../theme/dropdowns/ShValidatedInputDropdown';
import { useGetRelationsSelectQuery } from '../../../../../../newapi/global/relation/relationSlice';
import EMGeneralExplainerBox from '../../../../ModalsPopover/explainers/EMGeneralExplainerBox';
import * as yup from "yup";
import { yupRequired } from '../../../../../../utils/validation';
import { getMarketplaceCapabilities, getSyncOptionConfigurations } from '../../utils/etailize/etailizeHelpers';
import { Cancel, CheckCircle } from '@mui/icons-material';
import Subheading from '../../../../../theme/text/Subheading';

/**
 * EtailizeCredentials Component
 * 
 * Handles credential fields specific to Etailize integrations:
 * - Relation selection (when isFulfilment is true)
 * - Description field (required)
 * - Shipping name field (required)
 * 
 * Props:
 * - handleDataUpdate: Function to update progression state
 * - handleNext: Function to navigate to next step
 * - handlePrevious: Function to navigate to previous step
 * - data: Current progression data
 * - errors: Validation errors object
 */
const Content = (props) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const colors = tokens(theme.palette.mode);
    const data = { ...props.data };
    const dispatch = useDispatch();
    const isFulfilment = useSelector((state) => state.settings.environment.isFulfilment);

    // Get marketplace capabilities
    const marketplace = data.etailize_marketplace;
    const capabilities = getMarketplaceCapabilities(marketplace);
    const syncOptions = getSyncOptionConfigurations(capabilities, t);

    // Fetch relations using the useGetRelationsQuery hook
    const { data: relationsData } = useGetRelationsSelectQuery();

    const handleInput = (key, val) => {
        props.handleDataUpdate({ [key]: val });
        dispatch(setTabErrors({ ...props.errors, [key]: '' }));
    };

    return (
        <Box>
            <Subheading sx={{ mb: 1 }}>
                {t('etailize_credentials')}
            </Subheading>


            {/* Relation Selection (only for fulfillment environments) */}
            {isFulfilment && (
                <Box 
                    sx={{
                        width: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 7,
                        mb: 1.5,
                        pt:1,
                    }}
                >
                    <Box
                        sx={{
                            width: 120,
                        }}
                    >
                        <Text>{t("relation")}</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex: 1,
                            backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`,
                            borderRadius: 4,
                            display: "flex"
                        }}
                    >
                        <ShValidatedInputDropdown
                            closeOnSelection
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search_relation_placeholder")
                            }}
                            name="relationId"
                            height={40}
                            displayName="name"
                            changeField="id"
                            selected={relationsData?.findIndex(relation => relation.id === data?.relationId) ?? 0}
                            noSelection={t("choose_relation")}
                            options={relationsData ?? []}
                            onChange={handleInput}
                            error={props.errors.relationId}
                        />
                    </Box>
                </Box>
            )}

            {/* Description Field */}
            <Box 
                sx={{
                    width: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 7,
                    mb: 1.5,
                    ...(isFulfilment ? { paddingTop: 1 } : {})
                }}
            >
                <Box
                    sx={{
                        width: 120,
                    }}
                >
                    <Text>{t("description")}</Text>
                </Box>
                
                <Box
                    sx={{
                        flex: 1,
                        backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`,
                        borderRadius: 4,
                        display: "flex"
                    }}
                >
                    <ShValidatedInput
                        name="description"
                        value={data.description ?? ""}
                        onChange={handleInput}
                        error={props.errors.description}
                        placeholder={t('etailize_description_placeholder')}
                    />
                </Box>
            </Box>

            {/* Shipping Name Field */}
            <Box 
                sx={{
                    width: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 7,
                    mb: 1.5,
                    paddingTop: 1
                }}
            >
                <Box
                    sx={{
                        width: 120,
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <Text>{t("shipping_name")}</Text>
                    <EMGeneralExplainerBox
                        title={t('shipping_name')}
                        text={t('em_shippingname_text')}
                    />
                </Box>
                
                <Box
                    sx={{
                        flex: 1,
                        backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`,
                        borderRadius: 4,
                        display: "flex"
                    }}
                >
                    <ShValidatedInput
                        name="shippingName"
                        value={data.shippingName ?? ""}
                        onChange={handleInput}
                        error={props.errors.shippingName}
                        placeholder={t('etailize_shipping_name_placeholder')}
                    />
                </Box>
            </Box>

         

            {/* Propagation Settings Summary */}
            <Box sx={{ 
                mt: 3, 
                p: 2, 
                borderRadius:3,
                // backgroundColor: colors.grey[50],
                // borderRadius: 2,
                // border: `1px solid ${colors.grey[200]}`

                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
            }}>
                <Text fontWeight="bold">
                    {marketplace?.name || t('not_selected')}
                </Text>
                {marketplace?.description && (
                    <Text fontSize={12} color="textSecondary" sx={{ mb: 2 }}>
                        {marketplace.description}
                    </Text>
                )}
                <br/>
                
                <Text fontSize={12} color="textSecondary" sx={{ mb: 2 }}>
                    {t('configured_sync_options')}
                </Text>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {syncOptions.map((option, index) => {
                        const isEnabled = data[option.key] === 'true' || data[option.key] === true;
                        const isAvailable = option.available;
                        
                        return (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    borderRadius: 5,
                                    p:0.5,
                                    backgroundColor: isEnabled && isAvailable ? colors.green[100] : colors.red[100]
                                }}
                            >
                    
                    
                                <Box
                                    sx={{
                                        width:24,
                                        height:24,
                                        position:"relative",
                                        background:isEnabled && isAvailable  ? colors.green[100] : colors.red[100],
                                        color:isEnabled && isAvailable ?  colors.green[400] : colors.red[400],
                                        display:'flex',
                                        alignItems:'center',
                                        justifyContent:"center",
                                        borderRadius:4,
                                        flexShrink:0,
                                    }}
                                >
                                    <option.newIcon fontSize="small"/>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Text fontSize={13} fontWeight="medium">
                                        {t(option.label)}
                                    </Text>

                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1,pr:1, }}>

                                    {isEnabled && isAvailable ? (
                                        <CheckCircle sx={{ color: colors.green[400], fontSize: 16 }} />
                                    ) : (
                                        <Cancel sx={{ color: colors.red[400], fontSize: 16 }} />
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                <Text fontSize={11} color="textSecondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                    {t('sync_settings_can_be_changed_later')}
                </Text>
            </Box>

            
        </Box>
    );
};

const EtailizeCredentials = (props) => {
    const { t } = useTranslation();

    // Validation schema for Etailize credentials
    const schema = yup.object({
        description: yupRequired('description', t),
        shippingName: yupRequired('shippingName', t),
        ...(props.isFulfilment ? {
            relationId: yupRequired('relationId', t)
        } : {})
    });

    return (
        <ProgressionTab 
            tabId="tabEtailizeCredentials" 
            subtitle={t('details')}
            step={4} 
            maxSteps={5}
            schema={schema}
            nextTab="tabEtailizeAuthentication"
        >
            <Content {...props} />
        </ProgressionTab>
    );
};

export default EtailizeCredentials;